import { useEffect, useState } from 'react';
import { useSellerAdsAgentConfig, type AdsAgentConfig } from '../../hooks/useSellerAdsAgent';
import { api } from '../../services/api';
import { useSellerSettings } from './sellerUi';
import { AutomationRow, SaveStatus, SettingsSection, useAutoSave } from './settingsUi';

// Aba "Rotinas de Ads": as tres rotinas automaticas das campanhas patrocinadas
// juntas e no mesmo formato. Antes "sincronizar" ficava numa aba e "guardrail" e
// "portfolio" em outra, misturados com as metas.

type Routines = Pick<AdsAgentConfig, 'autoSyncMetrics' | 'syncIntervalHours' | 'autoGuardrail' | 'guardrailIntervalHours' | 'autoPortfolio' | 'portfolioIntervalHours'>;

export function SellerAdsSyncSettingsView() {
  const { config, loading, saveConfig } = useSellerAdsAgentConfig();
  const [form, setForm] = useState<Routines | null>(null);
  const { status, schedule } = useAutoSave<AdsAgentConfig>(saveConfig);
  const { openSettings } = useSellerSettings();

  useEffect(() => {
    if (config && !form) {
      setForm({
        autoSyncMetrics: config.autoSyncMetrics,
        syncIntervalHours: config.syncIntervalHours,
        autoGuardrail: config.autoGuardrail,
        guardrailIntervalHours: config.guardrailIntervalHours,
        autoPortfolio: config.autoPortfolio,
        portfolioIntervalHours: config.portfolioIntervalHours,
      });
    }
  }, [config, form]);

  if (!form) return <p className="text-sm text-gray-500">{loading ? 'Carregando...' : 'Não foi possível carregar.'}</p>;

  const set = (patch: Partial<Routines>) => {
    setForm((f) => (f ? { ...f, ...patch } : f));
    schedule(patch, true);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><SaveStatus status={status} /></div>

      <SettingsSection title="O que roda sozinho" help="Do mais barato pro mais completo. Dá pra rodar tudo na mão pela tela Ads também.">
        <AutomationRow
          title="Atualizar campanhas"
          help="Puxa gasto, cliques, vendas e orçamento das campanhas da Shopee Ads e do Mercado Ads. Não consome créditos."
          enabled={form.autoSyncMetrics}
          onToggle={(v) => set({ autoSyncMetrics: v })}
          hours={form.syncIntervalHours}
          onHoursChange={(h) => set({ syncIntervalHours: h })}
          automationKey="ads-sync"
        />
        <AutomationRow
          title="Checar gastos"
          help="Atualiza as campanhas e avisa quando alguma está gastando sem vender, passou do limite de gasto, perdeu impressões ou pode crescer. Só gera alertas, não usa IA."
          enabled={form.autoGuardrail}
          onToggle={(v) => set({ autoGuardrail: v })}
          hours={form.guardrailIntervalHours}
          onHoursChange={(h) => set({ guardrailIntervalHours: h })}
          automationKey="ads-guardrail"
        />
        <AutomationRow
          title="Análise completa com IA"
          help="Faz tudo acima e ainda: a IA olha o conjunto de campanhas, diagnostica as prioritárias e sugere ações (orçamento, meta, pausar)."
          enabled={form.autoPortfolio}
          onToggle={(v) => set({ autoPortfolio: v })}
          hours={form.portfolioIntervalHours}
          onHoursChange={(h) => set({ portfolioIntervalHours: h })}
          automationKey="ads-portfolio"
          credit={{ featureKey: 'ads_campaign_analysis', unitLabel: 'campanha(s)', estimate: async () => (await api.estimateSellerAdsAgent()).count }}
        />
      </SettingsSection>

      <button type="button" onClick={() => openSettings('ads-regras')} className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-left hover:border-orange-300">
        <span className="block text-sm font-semibold text-gray-900">Metas e regras de Ads →</span>
        <span className="block text-xs text-gray-500">Quanto você aceita gastar, o que a IA pode fazer sozinha e o contexto do seu negócio.</span>
      </button>
    </div>
  );
}
